import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Container, Row, Col } from "reactstrap";
import styles from "./styles.scss";
import withStyles from "isomorphic-style-loader/withStyles";
import TopBar from "./components/topBar";
import LeftSideBar from "./components/leftSideBar";
import { serviceCaller, ServiceCallerProps } from "../../../../composition";
import { connect } from "react-redux";
import { firebaseConnect, isLoaded } from "react-redux-firebase";
import get from "lodash.get";
import { useIframeSectionSelection } from "../../../../../hooks/useIframeSectionSelection";
import { VariablesPerSection } from "./components/leftSideBar/types";
import { useSectionField } from "../../../../../hooks/useSectionField";
import { ScaleLoader } from "react-spinners";
import { PAGES, SECTIONS, GIVING_SECTION_IDS, NEWSFEED_SECTION_IDS, POST_DETAIL_LAYOUT, getPostDetailSectionIds } from "./constants";
import { getCurrentCustomization } from "../../../../../utils/firebase/customization";
import {
  applyGivingBannerInitValues,
  applyGivingBodyInitValues,
  applyPostDetailInitValues,
  buildCustomizationSnapshot,
  CustomizationEditorState,
  getVariablesFromIframe,
} from "./util";
import { useAutoSave } from "./useAutoSave";
import {
  resolvePostDetailBioImage,
} from "./components/leftSideBar/postDetail/constants";

export interface Project {
  id: string | null;
  name: string;
}
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  CHARITY_CUSTOMIZE_HUB_PATH,
  ONBOARDING_DASHBOARD_PATH,
} from "../../../../../utils/onboarding/stepNavigation";
import { publishCharityCustomization } from "../../../../../services/publishCharityCustomization";
import SnackbarNotification from "../../../../../toolkit/snackbarNotification";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useAutoSelectSection } from "../../../../../hooks/useAutoSelectSection";
import { usesExternalGivingPage } from "../../../../../utils/firebase/paymentSetup";
import {
  buildPreviewIframeSrc,
  getPageFromSearchParam,
  pageParamFromSelectedPage,
} from "./previewOrigin";
import {
  usePreviewLoadTimeouts,
  usePreviewOriginFallback,
  PREVIEW_LOAD_TIMEOUT_MS,
  PREVIEW_PRIMARY_INIT_TIMEOUT_MS,
  PREVIEW_FALLBACK_INIT_TIMEOUT_MS,
} from "../../../../../hooks/usePreviewOriginFallback";
import PreviewUnavailablePanel from "../../shared/PreviewUnavailablePanel";

interface Props extends ServiceCallerProps {
  firebase: any;
  charityId: string;
  charityName?: string;
  customization?: any;
  brandSetup?: any;
  whitelabelUrl?: string | null;
  customizationIframeOrigin?: string | null;
  fallbackConfigLoaded?: boolean;
  usesExternalGivingPage?: boolean;
}

const Layout: React.FC<Props> = ({
  firebase,
  charityId,
  charityName = "",
  customization,
  brandSetup,
  config,
  serviceAccessToken,
  whitelabelUrl,
  customizationIframeOrigin,
  fallbackConfigLoaded = false,
  usesExternalGivingPage: usesExternalGivingPageProp = false,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const desktopPreviewViewportRef = useRef<HTMLDivElement>(null);
  const [desktopPreviewScale, setDesktopPreviewScale] = useState(1);
  const {
    activeOrigin: iframeOrigin,
    loadState: previewLoadState,
    primaryOrigin,
    fallbackOrigin,
    originSource,
    handlePreviewReady,
    handlePreviewFailure,
  } = usePreviewOriginFallback(
    whitelabelUrl,
    customizationIframeOrigin,
    fallbackConfigLoaded
  );
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [selectedPage, setSelectedPage] = useState(() =>
    getPageFromSearchParam(
      searchParams.get("page"),
      usesExternalGivingPageProp
    )
  );
  const [selectedSection, setSelectedSection] = useState(SECTIONS.NONE);
  const availablePages = usesExternalGivingPageProp
    ? { NEWSFEED_PAGE: PAGES.NEWSFEED_PAGE, POST_DETAILS_PAGE: PAGES.POST_DETAILS_PAGE }
    : PAGES;
  const iframeSrc = buildPreviewIframeSrc(iframeOrigin, selectedPage);
  const isPostDetailsPage = selectedPage === PAGES.POST_DETAILS_PAGE;
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [dataInitialized, setDataInitialized] = useState(false);
  const [navigationComplete, setNavigationComplete] = useState(false);
  const previewMessagingOrigin =
    iframeLoaded && dataInitialized && iframeOrigin ? iframeOrigin : "";

  const previewInitTimeoutMs =
    originSource === "primary" &&
    fallbackOrigin &&
    fallbackOrigin !== primaryOrigin
      ? PREVIEW_PRIMARY_INIT_TIMEOUT_MS
      : PREVIEW_FALLBACK_INIT_TIMEOUT_MS;

  usePreviewLoadTimeouts(
    iframeOrigin,
    iframeLoaded,
    dataInitialized,
    handlePreviewFailure,
    PREVIEW_LOAD_TIMEOUT_MS,
    previewInitTimeoutMs
  );

  const [selectedSectionN, setSelectedSectionN] = useIframeSectionSelection(
    iframeRef,
    iframeOrigin || ""
  );
  const [isMobileView, setIsMobileView] = useState(false);
  const [isViewSwitching, setIsViewSwitching] = useState(false);
  const [isSavingAndPublishing, setIsSavingAndPublishing] = useState(false);
  const [postDetailLayout, setPostDetailLayout] = useState<string>(
    POST_DETAIL_LAYOUT.CENTERED
  );
  const { snackbarProps, showSnackbar } = useSnackbar();

  const [giftDesignationProjects, setGiftDesignationProjects] = useState<Project[]>([]);

  const updateProjectsInIframe = useCallback((projects: Project[]) => {
    if (!previewMessagingOrigin) {
      return;
    }
    try {
      iframeRef.current?.contentWindow?.postMessage(
        { type: "updateProjects", projects },
        previewMessagingOrigin
      );
    } catch {
      // iframe not ready
    }
  }, [previewMessagingOrigin]);

  const addGiftDesignation = useCallback((name: string) => {
    const newProject: Project = {
      id: `preview-${Date.now()}`,
      name,
    };
    const newProjects = [...giftDesignationProjects, newProject];
    setGiftDesignationProjects(newProjects);
    updateProjectsInIframe(newProjects);
  }, [giftDesignationProjects, updateProjectsInIframe]);

  const removeGiftDesignation = useCallback((index: number) => {
    const newProjects = giftDesignationProjects.filter((_, i) => i !== index);
    setGiftDesignationProjects(newProjects);
    updateProjectsInIframe(newProjects);
  }, [giftDesignationProjects, updateProjectsInIframe]);

  const updateGiftDesignationName = useCallback((index: number, newName: string) => {
    const newProjects = giftDesignationProjects.map((p, i) =>
      i === index ? { ...p, name: newName } : p
    );
    setGiftDesignationProjects(newProjects);
    updateProjectsInIframe(newProjects);
  }, [giftDesignationProjects, updateProjectsInIframe]);

  const {
    newsfeedNavbarLogo,
    setNewsfeedNavbarLogo,
    organizationName,
    setOrganizationName,
    newsfeedHeaderTitle,
    setNewsfeedHeaderTitle,
    siteFaviconIcon,
    setSiteFaviconIcon,
    newsfeedShowHeaderTitle,
    setNewsfeedShowHeaderTitle,
    newsfeedNavbarGiveButtonLabel,
    setNewsfeedNavbarGiveButtonLabel,
    newsfeedNavbarGiveButtonColor,
    setNewsfeedNavbarGiveButtonColor,
    newsfeedNavbarHighlightColor,
    setNewsfeedNavbarHighlightColor,
    setNewsfeedNavbarToggleColor,
    setNewsfeedNavbarToggleBarColor,
    newsfeedBodyHighlightedTextColor,
    setNewsfeedBodyHighlightedTextColorVariable,
    newsfeedBodyGiveButtonLabel,
    setNewsfeedBodyGiveButtonLabel,
    newsfeedBodyGiveButtonColor,
    setNewsfeedBodyGiveButtonColor,
    setNewsfeedBodyShareColor,
    newsfeedFooterFirstPart,
    setNewsfeedFooterFirstPart,
    newsfeedFooterSecondPart,
    setNewsfeedFooterSecondPart,
    newsfeedFooterVersion,
    setNewsfeedFooterVersion,
    givingBodyShowMonthlyDonationNudge,
    setGivingBodyShowMonthlyDonationNudge,
    givingBodyMonthlyDonationNudgeColor,
    setGivingBodyMonthlyDonationNudgeColor,
    givingBodyHeadersAndAmountColor,
    setGivingBodyHeadersAndAmountColor,
    setGivingBodySelectedAmountColor,
    setGivingBodySelectedFrequencyColor,
    setGivingBodyArrowColor,
    givingBodyInactiveTabColor,
    setGivingBodyInactiveTabColor,
    givingBodyOneTimeMessageText,
    setGivingBodyOneTimeMessageText,
    fetchInitialGivingBodyOneTimeMessageText,
    givingBodyMonthlyMessageText,
    setGivingBodyMonthlyMessageText,
    fetchInitialGivingBodyMonthlyMessageText,
    givingBodyAmount1,
    setGivingBodyAmount1,
    givingBodyAmount2,
    setGivingBodyAmount2,
    givingBodyAmount3,
    setGivingBodyAmount3,
    givingBodyAmount4,
    setGivingBodyAmount4,
    givingBannerImageDesktop,
    setGivingBannerImageDesktop,
    fetchGivingBannerImage,
    givingBannerImageMobile,
    setGivingBannerImageMobile,
    fetchGivingBannerImageMobile,
    givingBannerOverlayColor,
    setGivingBannerOverlayColor,
    givingBannerOverlayDisplay,
    setGivingBannerOverlayDisplay,
    givingBannerIncludeOverlay,
    setGivingBannerIncludeOverlay,
    givingBannerMainMessageDesktop,
    setGivingBannerMainMessageDesktop,
    fetchGivingBannerMainMessageDesktop,
    givingBannerMainMessageMobile,
    setGivingBannerMainMessageMobile,
    fetchGivingBannerMainMessageMobile,
    givingBannerMainMessageDesktopColor,
    setGivingBannerMainMessageDesktopColor,
    fetchGivingBannerMainMessageDesktopColor,
    givingBannerMainMessageMobileColor,
    setGivingBannerMainMessageMobileColor,
    fetchGivingBannerMainMessageMobileColor,
    givingBannerSecondaryMessageDesktop,
    setGivingBannerSecondaryMessageDesktop,
    fetchGivingBannerSecondaryMessageDesktop,
    givingBannerSecondaryMessageMobile,
    setGivingBannerSecondaryMessageMobile,
    fetchGivingBannerSecondaryMessageMobile,
    givingBannerSecondaryMessageDesktopColor,
    setGivingBannerSecondaryMessageDesktopColor,
    fetchGivingBannerSecondaryMessageDesktopColor,
    givingBannerSecondaryMessageMobileColor,
    setGivingBannerSecondaryMessageMobileColor,
    fetchGivingBannerSecondaryMessageMobileColor,
    setGivingBannerMainMessageAlignment,
    setGivingBannerMainMessageTransform,
    setGivingBannerSecondaryMessageAlignment,
    givingBannerMainMessageActiveButtons,
    setGivingBannerMainMessageActiveButtons,
    givingBannerSecondaryMessageActiveButtons,
    setGivingBannerSecondaryMessageActiveButtons,
    clickOneTime,
    clickMonthly,
    givingSideParagraphText,
    setGivingSideParagraphText,
    givingSideParagraphTextColor,
    setGivingSideParagraphTextColor,
    setGivingSideParagraphStrongTextColor,
    postDetailGiveButtonLabel,
    setPostDetailGiveButtonLabel,
    postNormalCase,
    setPostNormalCase,
    postDetailBioDescription,
    setPostDetailBioDescription,
    postDetailBioImage,
    setPostDetailBioImage,
    fetchPostDetailBioImage,
  } = getVariablesFromIframe(
    SECTIONS,
    useSectionField,
    iframeRef,
    previewMessagingOrigin,
    selectedSection
  );

  const getCustomizationEditorState = useCallback((): CustomizationEditorState => ({
    organizationName,
    newsfeedHeaderTitle,
    newsfeedShowHeaderTitle,
    newsfeedNavbarLogo,
    siteFaviconIcon,
    newsfeedNavbarGiveButtonLabel,
    newsfeedNavbarGiveButtonColor,
    newsfeedNavbarHighlightColor,
    newsfeedBodyGiveButtonLabel,
    newsfeedBodyGiveButtonColor,
    newsfeedBodyShareColor: newsfeedNavbarHighlightColor,
    newsfeedFooterFirstPart,
    newsfeedFooterSecondPart,
    givingBannerImageDesktop,
    givingBannerImageMobile,
    givingBannerOverlayDisplay,
    givingBannerMainMessageDesktop,
    givingBannerMainMessageMobile,
    givingBannerMainMessageDesktopColor,
    givingBannerMainMessageMobileColor,
    givingBannerSecondaryMessageDesktop,
    givingBannerSecondaryMessageMobile,
    givingBannerSecondaryMessageDesktopColor,
    givingBannerSecondaryMessageMobileColor,
    givingBannerMainMessageActiveButtons,
    givingBannerSecondaryMessageActiveButtons,
    givingBodyHeadersAndAmountColor,
    givingBodyInactiveTabColor,
    givingBodyShowMonthlyDonationNudge,
    givingBodyMonthlyDonationNudgeColor,
    givingBodyOneTimeMessageText,
    givingBodyMonthlyMessageText,
    givingBodyAmount1,
    givingBodyAmount2,
    givingBodyAmount3,
    givingBodyAmount4,
    givingSideParagraphText,
    givingSideParagraphTextColor,
    giftDesignationProjects,
    postDetailLayout,
    postDetailGiveButtonLabel,
    postDetailBioDescription,
    postDetailBioImage,
    postNormalCase,
  }), [
    organizationName,
    newsfeedHeaderTitle,
    newsfeedShowHeaderTitle,
    newsfeedNavbarLogo,
    siteFaviconIcon,
    newsfeedNavbarGiveButtonLabel,
    newsfeedNavbarGiveButtonColor,
    newsfeedNavbarHighlightColor,
    newsfeedBodyGiveButtonLabel,
    newsfeedBodyGiveButtonColor,
    newsfeedFooterFirstPart,
    newsfeedFooterSecondPart,
    givingBannerImageDesktop,
    givingBannerImageMobile,
    givingBannerOverlayDisplay,
    givingBannerMainMessageDesktop,
    givingBannerMainMessageMobile,
    givingBannerMainMessageDesktopColor,
    givingBannerMainMessageMobileColor,
    givingBannerSecondaryMessageDesktop,
    givingBannerSecondaryMessageMobile,
    givingBannerSecondaryMessageDesktopColor,
    givingBannerSecondaryMessageMobileColor,
    givingBannerMainMessageActiveButtons,
    givingBannerSecondaryMessageActiveButtons,
    givingBodyHeadersAndAmountColor,
    givingBodyInactiveTabColor,
    givingBodyShowMonthlyDonationNudge,
    givingBodyMonthlyDonationNudgeColor,
    givingBodyOneTimeMessageText,
    givingBodyMonthlyMessageText,
    givingBodyAmount1,
    givingBodyAmount2,
    givingBodyAmount3,
    givingBodyAmount4,
    givingSideParagraphText,
    givingSideParagraphTextColor,
    giftDesignationProjects,
    postDetailLayout,
    postDetailGiveButtonLabel,
    postDetailBioDescription,
    postDetailBioImage,
    postNormalCase,
  ]);

  const customizationSnapshot = useMemo(() => {
    if (!dataInitialized) {
      return {};
    }
    return buildCustomizationSnapshot(getCustomizationEditorState());
  }, [dataInitialized, getCustomizationEditorState]);

  const variablesPerSection: VariablesPerSection = {
    [SECTIONS.NEWSFEED_NAVBAR.id]: {
      logo: [newsfeedNavbarLogo, setNewsfeedNavbarLogo],
      favicon: [siteFaviconIcon, setSiteFaviconIcon],
      showHeaderTitle: [newsfeedShowHeaderTitle, setNewsfeedShowHeaderTitle],
      headerTitle: [newsfeedHeaderTitle, setNewsfeedHeaderTitle],
      organizationName: [organizationName, setOrganizationName],
      giveButtonLabel: [
        newsfeedNavbarGiveButtonLabel,
        setNewsfeedNavbarGiveButtonLabel,
      ],
      giveButtonColor: [
        newsfeedNavbarGiveButtonColor,
        setNewsfeedNavbarGiveButtonColor,
      ],
      highlightColor: [
        newsfeedNavbarHighlightColor,
        setNewsfeedNavbarHighlightColor,
      ],
    },
    [SECTIONS.NEWSFEED_BODY.id]: {
      highlightedTextColor: [
        newsfeedBodyHighlightedTextColor,
        setNewsfeedBodyHighlightedTextColorVariable,
      ],
      giveButtonLabel: [
        newsfeedBodyGiveButtonLabel,
        setNewsfeedBodyGiveButtonLabel,
      ],
      giveButtonColor: [
        newsfeedBodyGiveButtonColor,
        setNewsfeedBodyGiveButtonColor,
      ],
    },
    [SECTIONS.NEWSFEED_FOOTER.id]: {
      firstPart: [
        newsfeedFooterFirstPart.replace(
          new Date().getFullYear().toString(),
          "YEAR"
        ),
        setNewsfeedFooterFirstPart,
      ],
      secondPart: [
        newsfeedFooterSecondPart.replace(
          new Date().getFullYear().toString(),
          "YEAR"
        ),
        setNewsfeedFooterSecondPart,
      ],
      version: [
        newsfeedFooterVersion.replace(
          new Date().getFullYear().toString(),
          "YEAR"
        ),
        setNewsfeedFooterVersion,
      ],
    },
    [SECTIONS.GIVING_BODY.id]: {
      showMonthlyDonationNudge: [
        givingBodyShowMonthlyDonationNudge,
        setGivingBodyShowMonthlyDonationNudge,
      ],
      monthlyDonationNudgeColor: [
        givingBodyMonthlyDonationNudgeColor,
        setGivingBodyMonthlyDonationNudgeColor,
      ],
      headersAndAmountColor: [
        givingBodyHeadersAndAmountColor,
        setGivingBodyHeadersAndAmountColor,
      ],
      inactiveTabColor: [
        givingBodyInactiveTabColor,
        setGivingBodyInactiveTabColor,
      ],
      oneTimeMessageText: [
        givingBodyOneTimeMessageText,
        setGivingBodyOneTimeMessageText,
      ],
      monthlyMessageText: [
        givingBodyMonthlyMessageText,
        setGivingBodyMonthlyMessageText,
      ],
      amount1: [givingBodyAmount1, setGivingBodyAmount1],
      amount2: [givingBodyAmount2, setGivingBodyAmount2],
      amount3: [givingBodyAmount3, setGivingBodyAmount3],
      amount4: [givingBodyAmount4, setGivingBodyAmount4],
      ["clickOneTime"]: clickOneTime,
      ["clickMonthly"]: clickMonthly,
      ["fetchGivingBodyOneTimeMessageText"]: fetchInitialGivingBodyOneTimeMessageText,
      ["fetchGivingBodyMonthlyMessageText"]: fetchInitialGivingBodyMonthlyMessageText,
      giftDesignationProjects: giftDesignationProjects,
      giftDesignationLoading: !dataInitialized,
      addGiftDesignation: addGiftDesignation,
      removeGiftDesignation: removeGiftDesignation,
      updateGiftDesignationName: updateGiftDesignationName,
      isMobileView: [isMobileView, setIsMobileView],
    },
    [SECTIONS.GIVING_BANNER.id]: {
      bannerImageDesktop: [
        givingBannerImageDesktop,
        setGivingBannerImageDesktop,
      ],
      ["fetchGivingBannerImage"]: fetchGivingBannerImage,
      ["fetchGivingBannerImageMobile"]: fetchGivingBannerImageMobile,
      bannerImageMobile: [givingBannerImageMobile, setGivingBannerImageMobile],
      includeOverlay: [
        givingBannerIncludeOverlay,
        setGivingBannerIncludeOverlay,
      ],
      overlayColor: [givingBannerOverlayColor, setGivingBannerOverlayColor],
      mainMessageDesktop: [
        givingBannerMainMessageDesktop,
        setGivingBannerMainMessageDesktop,
      ],
      ["fetchGivingBannerMainMessageDesktop"]: fetchGivingBannerMainMessageDesktop,
      mainMessageActiveButtons: [
        givingBannerMainMessageActiveButtons,
        setGivingBannerMainMessageActiveButtons,
      ],
      mainMessageDesktopColor: [
        givingBannerMainMessageDesktopColor,
        setGivingBannerMainMessageDesktopColor,
      ],
      ["fetchGivingBannerMainMessageDesktopColor"]:
        fetchGivingBannerMainMessageDesktopColor,
      mainMessageMobile: [
        givingBannerMainMessageMobile,
        setGivingBannerMainMessageMobile,
      ],
      ["fetchGivingBannerMainMessageMobile"]: fetchGivingBannerMainMessageMobile,
      mainMessageMobileColor: [
        givingBannerMainMessageMobileColor,
        setGivingBannerMainMessageMobileColor,
      ],
      ["fetchGivingBannerMainMessageMobileColor"]:
        fetchGivingBannerMainMessageMobileColor,
      secondaryMessageDesktop: [
        givingBannerSecondaryMessageDesktop,
        setGivingBannerSecondaryMessageDesktop,
      ],
      ["fetchGivingBannerSecondaryMessageDesktop"]:
        fetchGivingBannerSecondaryMessageDesktop,
      secondaryMessageDesktopColor: [
        givingBannerSecondaryMessageDesktopColor,
        setGivingBannerSecondaryMessageDesktopColor,
      ],
      ["fetchGivingBannerSecondaryMessageDesktopColor"]:
        fetchGivingBannerSecondaryMessageDesktopColor,
      secondaryMessageMobile: [
        givingBannerSecondaryMessageMobile,
        setGivingBannerSecondaryMessageMobile,
      ],
      ["fetchGivingBannerSecondaryMessageMobile"]:
        fetchGivingBannerSecondaryMessageMobile,
      secondaryMessageMobileColor: [
        givingBannerSecondaryMessageMobileColor,
        setGivingBannerSecondaryMessageMobileColor,
      ],
      ["fetchGivingBannerSecondaryMessageMobileColor"]:
        fetchGivingBannerSecondaryMessageMobileColor,
      secondaryMessageActiveButtons: [
        givingBannerSecondaryMessageActiveButtons,
        setGivingBannerSecondaryMessageActiveButtons,
      ],
      isMobileView: [isMobileView, setIsMobileView],
    },
    [SECTIONS.GIVING_SIDE_PARAGRAPH.id]: {
      text: [givingSideParagraphText, setGivingSideParagraphText],
      textColor: [
        givingSideParagraphTextColor,
        setGivingSideParagraphTextColor,
      ],
    },
    [SECTIONS.POST_DETAIL_MAIN.id]: {
      giveButtonLabel: [
        postDetailGiveButtonLabel,
        setPostDetailGiveButtonLabel,
      ],
      postNormalCase: [postNormalCase, setPostNormalCase],
    },
    [SECTIONS.POST_DETAIL_BIO.id]: {
      bioDescription: [
        postDetailBioDescription,
        setPostDetailBioDescription,
      ],
      bioImage: [postDetailBioImage, setPostDetailBioImage],
      fetchBioImage: fetchPostDetailBioImage,
    },
  };

  const saveCustomization = async newData => {
    const filteredData = Object.entries(newData).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});

    return await firebase.update(
      `${charityId}/charitySetup/customization`,
      filteredData
    );
  };

  const sendMessage = useCallback(
    (type: string, payload: Record<string, unknown>) => {
      const win = iframeRef.current?.contentWindow;
      if (!win || !iframeOrigin || !iframeLoaded) {
        return;
      }
      try {
        win.postMessage({ type, ...payload }, iframeOrigin);
      } catch {
        // iframe may still be about:blank before cross-origin navigation completes
      }
    },
    [iframeOrigin, iframeLoaded]
  );

  const handlePageChange = useCallback(
    (page: string) => {
      if (page === selectedPage) {
        return;
      }

      if (
        usesExternalGivingPageProp &&
        page === PAGES.GIVING_PAGE
      ) {
        return;
      }

      setSelectedPage(page);
      setSelectedSection(SECTIONS.NONE);

      const fromHub = searchParams.get("from") === "hub" ? "&from=hub" : "";
      navigate(
        `/onboarding/customation?page=${pageParamFromSelectedPage(page)}${fromHub}`,
        { replace: true }
      );
    },
    [selectedPage, navigate, usesExternalGivingPageProp, searchParams]
  );

  const performSave = useCallback(async () => {
    const snapshot = buildCustomizationSnapshot(getCustomizationEditorState());
    await saveCustomization(snapshot);

    const currentPageName =
      selectedPage === PAGES.POST_DETAILS_PAGE
        ? "postDetails"
        : pageParamFromSelectedPage(selectedPage);
    const metadataUpdate = {
      [`${currentPageName}Customized`]: true,
      [`${currentPageName}LastSaved`]: Date.now(),
      lastCustomizedPage: currentPageName,
    };

    await firebase.update(
      `${charityId}/charitySetup/brandSetup`,
      metadataUpdate
    );
  }, [charityId, getCustomizationEditorState, firebase, selectedPage]);

  const { status: autoSaveStatus, saveNow } = useAutoSave({
    data: customizationSnapshot,
    onSave: performSave,
    debounceMs: 1500,
    enabled: previewLoadState === "ready" && dataInitialized,
    onError: () => {
      showSnackbar("There was an error saving your changes. Please try again later.", "error");
    },
  });

  const saveAndPublish = useCallback(async () => {
    try {
      setIsSavingAndPublishing(true);

      const snapshot = buildCustomizationSnapshot(getCustomizationEditorState());
      await saveCustomization(snapshot);

      const currentPageName =
        selectedPage === PAGES.GIVING_PAGE
          ? "giving"
          : selectedPage === PAGES.POST_DETAILS_PAGE
          ? "postDetails"
          : "newsfeed";
      await firebase.update(`${charityId}/charitySetup/brandSetup`, {
        [`${currentPageName}Customized`]: true,
        [`${currentPageName}LastSaved`]: Date.now(),
        lastCustomizedPage: currentPageName,
      });

      await publishCharityCustomization({
        charityId,
        config,
        serviceAccessToken,
      });

      showSnackbar("Your changes have been saved and deployment has been triggered! The changes will be live in a few minutes.", "success");
    } catch (error) {
      showSnackbar("There was an error publishing your changes. Please try again later.", "error");
      console.error("Error publishing changes:", error);
    } finally {
      setIsSavingAndPublishing(false);
    }
  }, [charityId, getCustomizationEditorState, config, serviceAccessToken, selectedPage, firebase]);

  const handleBackToSetup = useCallback(async () => {
    await saveNow();
    const fromHub = searchParams.get("from") === "hub";
    navigate(
      fromHub
        ? `${CHARITY_CUSTOMIZE_HUB_PATH}/website`
        : `${ONBOARDING_DASHBOARD_PATH}/branding`
    );
  }, [saveNow, navigate, searchParams]);

  useEffect(() => {
    if (!customization) {
      return;
    }
    if (customization.organizationName) {
      setOrganizationName(customization.organizationName);
    }
    if (customization.newsfeedHeaderTitle) {
      setNewsfeedHeaderTitle(customization.newsfeedHeaderTitle);
    } else if (customization.organizationName) {
      setNewsfeedHeaderTitle(customization.organizationName);
    }
    if (customization.siteFaviconIcon) {
      setSiteFaviconIcon(customization.siteFaviconIcon);
    }
    if (customization.newsfeedShowHeaderTitle !== undefined) {
      setNewsfeedShowHeaderTitle(!!customization.newsfeedShowHeaderTitle);
    }
    if (customization.postDetailLayout) {
      setPostDetailLayout(String(customization.postDetailLayout));
    }
    if (customization.postNormalCase !== undefined) {
      setPostNormalCase(!!customization.postNormalCase);
    }
    if (customization.givingBodyShowMonthlyDonationNudge !== undefined) {
      setGivingBodyShowMonthlyDonationNudge(
        !!customization.givingBodyShowMonthlyDonationNudge
      );
    }
  }, [
    customization,
    setOrganizationName,
    setNewsfeedHeaderTitle,
    setSiteFaviconIcon,
    setNewsfeedShowHeaderTitle,
    setPostNormalCase,
    setGivingBodyShowMonthlyDonationNudge,
  ]);

  useEffect(() => {
    if (
      !iframeLoaded ||
      customization === undefined ||
      brandSetup === undefined
    ) {
      return undefined;
    }

    if (dataInitialized) {
      return undefined;
    }

    const data = getCurrentCustomization(
      customization,
      brandSetup,
      saveCustomization
    );
    if (!data) {
      return undefined;
    }

    const expectedOrigin = iframeOrigin;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 20;
    let retryTimer: number | undefined;

    const onMessage = (e: MessageEvent) => {
      if (expectedOrigin && e.origin !== expectedOrigin) {
        return;
      }
      const msg = e.data || {};
      if (msg.type !== "initializationData") {
        return;
      }

      if (data.newsfeedNavbarLogo) {
        setNewsfeedNavbarLogo(data.newsfeedNavbarLogo);
      }
      if (data.newsfeedNavbarGiveButtonLabel) {
        setNewsfeedNavbarGiveButtonLabel(data.newsfeedNavbarGiveButtonLabel);
      }
      if (data.newsfeedNavbarGiveButtonColor) {
        setNewsfeedNavbarGiveButtonColor(data.newsfeedNavbarGiveButtonColor);
      }
      if (data.newsfeedNavbarHighlightColor) {
        setNewsfeedNavbarHighlightColor(data.newsfeedNavbarHighlightColor);
      }
      if (data.siteFaviconIcon) {
        setSiteFaviconIcon(data.siteFaviconIcon);
      }
      if (
        data.newsfeedShowHeaderTitle !== undefined &&
        data.newsfeedShowHeaderTitle !== null
      ) {
        setNewsfeedShowHeaderTitle(!!data.newsfeedShowHeaderTitle);
      }
      if (data.organizationName) {
        setOrganizationName(data.organizationName);
      }
      if (data.newsfeedHeaderTitle) {
        setNewsfeedHeaderTitle(data.newsfeedHeaderTitle);
      } else if (data.organizationName) {
        setNewsfeedHeaderTitle(data.organizationName);
      }

      applyGivingBannerInitValues(data, msg.values, {
        setGivingBannerImageDesktop,
        setGivingBannerImageMobile,
        setGivingBannerOverlayColor,
        setGivingBannerOverlayDisplay,
        setGivingBannerIncludeOverlay,
        setGivingBannerMainMessageDesktop,
        setGivingBannerMainMessageMobile,
        setGivingBannerMainMessageDesktopColor,
        setGivingBannerMainMessageMobileColor,
        setGivingBannerSecondaryMessageDesktop,
        setGivingBannerSecondaryMessageMobile,
        setGivingBannerSecondaryMessageDesktopColor,
        setGivingBannerSecondaryMessageMobileColor,
        setGivingBannerMainMessageActiveButtons,
        setGivingBannerSecondaryMessageActiveButtons,
      });

      const iframeValues = (msg.values || {}) as Record<string, unknown>;
      const bannerDesktop =
        (iframeValues.givingBannerImageDesktop as string) ||
        (data.givingBannerImageDesktop as string);
      if (bannerDesktop) {
        sendMessage("updateStyle", {
          sectionId: SECTIONS.GIVING_BANNER.id,
          selector: ".topBanner",
          style: {
            "background-image": `url('${String(bannerDesktop).replace(/'/g, "\\'")}')`,
          },
        });
      }
      const bannerMobile =
        (iframeValues.givingBannerImageMobile as string) ||
        (data.givingBannerImageMobile as string);
      if (bannerMobile) {
        sendMessage("updateStyle", {
          sectionId: SECTIONS.GIVING_BANNER.id,
          selector: ".topBanner",
          style: {
            "background-image": `url('${String(bannerMobile).replace(/'/g, "\\'")}')`,
          },
          mediaQuery: "(max-width: 992px)",
        });
      }
      const navbarLogo =
        (iframeValues.newsfeedNavbarLogo as string) ||
        (data.newsfeedNavbarLogo as string);
      if (navbarLogo) {
        sendMessage("updateStyle", {
          sectionId: SECTIONS.NEWSFEED_NAVBAR.id,
          selector: ".logo-web",
          style: {
            background: `url('${String(navbarLogo).replace(/'/g, "\\'")}') no-repeat center / contain`,
          },
        });
      }

      applyGivingBodyInitValues(data, msg.values, {
        setGivingBodyOneTimeMessageText,
        setGivingBodyMonthlyMessageText,
        setGivingBodyHeadersAndAmountColor,
        setGivingBodyInactiveTabColor,
        setGivingBodyShowMonthlyDonationNudge,
        setGivingBodyMonthlyDonationNudgeColor,
        setGivingBodyAmount1,
        setGivingBodyAmount2,
        setGivingBodyAmount3,
        setGivingBodyAmount4,
      });

      applyPostDetailInitValues(data, msg.values, {
        setPostDetailGiveButtonLabel,
        setPostNormalCase,
        setPostDetailBioDescription,
        setPostDetailBioImage,
        setPostDetailLayout,
      });

      const bioImage = resolvePostDetailBioImage(
        (iframeValues.postDetailBioImage as string) ||
          (data.postDetailBioImage as string)
      );
      const initLayout =
        (iframeValues.postDetailLayout as string) ||
        (data.postDetailLayout as string) ||
        POST_DETAIL_LAYOUT.CENTERED;
      if (initLayout === POST_DETAIL_LAYOUT.BIO) {
        sendMessage("updateStyle", {
          sectionId: SECTIONS.POST_DETAIL_BIO.id,
          selector: ".prime-image-description",
          style: {
            "background-image": `url('${String(bioImage).replace(/'/g, "\\'")}')`,
          },
        });
      }

      const projects =
        data.giftDesignationProjects &&
        Array.isArray(data.giftDesignationProjects) &&
        data.giftDesignationProjects.length > 0
          ? data.giftDesignationProjects
          : msg.values.giftDesignationProjects || [];
      setGiftDesignationProjects(projects);

      setDataInitialized(true);
    };

    window.addEventListener("message", onMessage);

    const pushInitialize = () => {
      if (cancelled || dataInitialized) {
        return;
      }
      sendMessage("initialize", { values: data });
      attempts += 1;
      if (attempts < maxAttempts) {
        retryTimer = window.setTimeout(pushInitialize, 1000);
      }
    };

    retryTimer = window.setTimeout(pushInitialize, 500);

    return () => {
      cancelled = true;
      if (retryTimer !== undefined) {
        window.clearTimeout(retryTimer);
      }
      window.removeEventListener("message", onMessage);
    };
  }, [
    iframeLoaded,
    iframeOrigin,
    selectedPage,
    customization,
    brandSetup,
    dataInitialized,
    sendMessage,
  ]);

  useEffect(() => {
    setIframeLoaded(false);
    setDataInitialized(false);
    setNavigationComplete(false);
    setIsViewSwitching(false);
    setSelectedSectionN(null);
    setSelectedSection(SECTIONS.NONE);
  }, [iframeSrc, setSelectedSectionN]);

  useEffect(() => {
    if (!selectedSectionN) {
      return;
    }

    const validSectionIds =
      selectedPage === PAGES.GIVING_PAGE
        ? GIVING_SECTION_IDS
        : selectedPage === PAGES.POST_DETAILS_PAGE
        ? getPostDetailSectionIds(postDetailLayout)
        : NEWSFEED_SECTION_IDS;

    if (!validSectionIds.has(selectedSectionN)) {
      if (
        selectedPage === PAGES.POST_DETAILS_PAGE &&
        selectedSectionN === "post-detail-latest-news"
      ) {
        setSelectedSection(SECTIONS.POST_DETAIL_MAIN);
        setSelectedSectionN(SECTIONS.POST_DETAIL_MAIN.id);
        sendMessage("highlight", { sectionId: SECTIONS.POST_DETAIL_MAIN.id });
      }
      return;
    }

    const section = Object.values(SECTIONS).find(
      sec => sec.id === selectedSectionN
    );
    if (section) {
      setSelectedSection(section);
    }
  }, [selectedSectionN, selectedPage, postDetailLayout]);

  useEffect(() => {
    if (!dataInitialized) {
      setNavigationComplete(false);
      return undefined;
    }

    const timer = setTimeout(() => {
      setNavigationComplete(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [dataInitialized]);

  useEffect(() => {
    if (navigationComplete) {
      handlePreviewReady();
    }
  }, [navigationComplete, handlePreviewReady]);

  useEffect(() => {
    if (!dataInitialized || selectedSection.id === SECTIONS.NONE.id) {
      return;
    }
    const snapshot = buildCustomizationSnapshot(getCustomizationEditorState());
    sendMessage("initialize", { values: snapshot });
  }, [selectedSection.id, dataInitialized, sendMessage]);

  useEffect(() => {
    if (!dataInitialized) {
      return;
    }
    sendMessage("initialize", {
      values: {
        newsfeedShowHeaderTitle,
        newsfeedHeaderTitle,
        organizationName,
        givingBodyShowMonthlyDonationNudge,
        givingBodyMonthlyDonationNudgeColor,
      },
    });
  }, [
    newsfeedShowHeaderTitle,
    newsfeedHeaderTitle,
    organizationName,
    givingBodyShowMonthlyDonationNudge,
    givingBodyMonthlyDonationNudgeColor,
    dataInitialized,
    sendMessage,
  ]);

  useEffect(() => {
    if (!dataInitialized || !givingBodyMonthlyDonationNudgeColor) {
      return;
    }
    sendMessage("updateStyle", {
      sectionId: SECTIONS.GIVING_BODY.id,
      selector: ".monthly-gift",
      style: {
        "background-color": givingBodyMonthlyDonationNudgeColor,
      },
      applyToAll: true,
    });
  }, [
    givingBodyMonthlyDonationNudgeColor,
    dataInitialized,
    sendMessage,
  ]);

  useEffect(() => {
    if (isMobileView) {
      setDesktopPreviewScale(1);
      return undefined;
    }

    const viewport = desktopPreviewViewportRef.current;
    if (!viewport) {
      return undefined;
    }

    const updateScale = () => {
      const widthScale = viewport.clientWidth / 1280;
      const heightScale = viewport.clientHeight / 800;
      setDesktopPreviewScale(Math.min(widthScale, heightScale, 1));
    };

    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [isMobileView]);

  useEffect(() => {
    if (!dataInitialized || !givingBannerImageDesktop) {
      return;
    }
    sendMessage("updateStyle", {
      sectionId: SECTIONS.GIVING_BANNER.id,
      selector: ".topBanner",
      style: {
        "background-image": `url('${String(givingBannerImageDesktop).replace(/'/g, "\\'")}')`,
      },
    });
  }, [givingBannerImageDesktop, dataInitialized, sendMessage]);

  useEffect(() => {
    if (!dataInitialized || !givingBannerImageMobile) {
      return;
    }
    sendMessage("updateStyle", {
      sectionId: SECTIONS.GIVING_BANNER.id,
      selector: ".topBanner",
      style: {
        "background-image": `url('${String(givingBannerImageMobile).replace(/'/g, "\\'")}')`,
      },
      mediaQuery: "(max-width: 992px)",
    });
  }, [givingBannerImageMobile, dataInitialized, sendMessage]);

  useEffect(() => {
    setGivingBannerMainMessageAlignment(
      givingBannerMainMessageActiveButtons.textAlignment
    );
    setGivingBannerMainMessageTransform(
      givingBannerMainMessageActiveButtons.textFormat
    );
    setGivingBannerSecondaryMessageAlignment(
      givingBannerSecondaryMessageActiveButtons.textAlignment
    );
  }, [
    givingBannerMainMessageActiveButtons,
    givingBannerSecondaryMessageActiveButtons,
  ]);

  useEffect(() => {
    if (!givingBannerIncludeOverlay) {
      setGivingBannerOverlayDisplay("none");
      return;
    }
    setGivingBannerOverlayDisplay("inset 0 0 0 10000px");
  }, [givingBannerIncludeOverlay, setGivingBannerOverlayDisplay]);

  useEffect(() => {
    setNewsfeedBodyGiveButtonColor(newsfeedNavbarGiveButtonColor);
  }, [newsfeedNavbarGiveButtonColor]);

  useEffect(() => {
    setNewsfeedBodyShareColor(newsfeedNavbarHighlightColor);
    setNewsfeedNavbarToggleBarColor(newsfeedNavbarHighlightColor);
    setNewsfeedNavbarToggleColor(newsfeedNavbarHighlightColor);
  }, [newsfeedNavbarHighlightColor]);

  useEffect(() => {
    setGivingBodySelectedFrequencyColor(givingBodyHeadersAndAmountColor);
    setGivingBodySelectedAmountColor(givingBodyHeadersAndAmountColor);
    setGivingBodyArrowColor(givingBodyHeadersAndAmountColor);
  }, [givingBodyHeadersAndAmountColor]);

  useEffect(() => {
    setGivingSideParagraphStrongTextColor(givingSideParagraphTextColor);
  }, [givingSideParagraphTextColor]);

  const handleAutoSelection = useCallback((sectionToSelect) => {
    setSelectedSection(sectionToSelect);
    setSelectedSectionN(sectionToSelect.id);
    sendMessage("highlight", { sectionId: sectionToSelect.id });
  }, [sendMessage, setSelectedSectionN]);

  // Wait for navigationComplete instead of just dataInitialized to avoid race condition
  useAutoSelectSection({
    iframeLoaded: previewLoadState === "ready",
    dataInitialized: navigationComplete,
    selectedSection,
    selectedPage,
    onSectionSelect: handleAutoSelection
  });

  const handlePostDetailLayoutChange = useCallback(
    (layout: string) => {
      setPostDetailLayout(layout);
      const validIds = getPostDetailSectionIds(layout);
      if (!validIds.has(selectedSection.id)) {
        setSelectedSection(SECTIONS.POST_DETAIL_MAIN);
        setSelectedSectionN(SECTIONS.POST_DETAIL_MAIN.id);
        sendMessage("highlight", { sectionId: SECTIONS.POST_DETAIL_MAIN.id });
      }
    },
    [selectedSection.id, sendMessage, setSelectedSectionN]
  );

  useEffect(() => {
    if (!dataInitialized || !isPostDetailsPage) {
      return;
    }
    sendMessage("initialize", {
      values: {
        postDetailLayout,
        postNormalCase,
      },
    });
    const badgeTimer = window.setTimeout(() => {
      sendMessage("refreshSectionBadges", {});
    }, 400);
    return () => window.clearTimeout(badgeTimer);
  }, [
    postDetailLayout,
    postNormalCase,
    dataInitialized,
    isPostDetailsPage,
    sendMessage,
  ]);

  useEffect(() => {
    if (!navigationComplete || !isPostDetailsPage) {
      return undefined;
    }
    const badgeTimer = window.setTimeout(() => {
      sendMessage("refreshSectionBadges", {});
    }, 200);
    return () => window.clearTimeout(badgeTimer);
  }, [navigationComplete, isPostDetailsPage, sendMessage]);

  useEffect(() => {
    if (
      !dataInitialized ||
      !isPostDetailsPage ||
      postDetailLayout !== POST_DETAIL_LAYOUT.BIO
    ) {
      return;
    }
    const bioImageUrl = resolvePostDetailBioImage(postDetailBioImage);
    sendMessage("updateStyle", {
      sectionId: SECTIONS.POST_DETAIL_BIO.id,
      selector: ".prime-image-description",
      style: {
        "background-image": `url('${String(bioImageUrl).replace(/'/g, "\\'")}')`,
      },
    });
  }, [
    postDetailBioImage,
    postDetailLayout,
    dataInitialized,
    isPostDetailsPage,
    sendMessage,
  ]);

  const handleSwitchView = useCallback(() => {
    setIsViewSwitching(true);
    sendMessage("switchView", {
      view: isMobileView ? "web" : "mobile",
    });
    setIsMobileView(prev => !prev);

    const sectionId =
      selectedSection.id !== SECTIONS.NONE.id
        ? selectedSection.id
        : selectedSectionN;
    if (sectionId && sectionId !== SECTIONS.NONE.id) {
      window.setTimeout(() => {
        sendMessage("highlight", { sectionId });
      }, 150);
    }
  }, [
    isMobileView,
    sendMessage,
    selectedSection.id,
    selectedSectionN,
  ]);

  const previewReady =
    previewLoadState === "ready" &&
    iframeLoaded &&
    navigationComplete;
  const previewFailed = previewLoadState === "failed";
  const previewLoading = !previewReady && !previewFailed;

  useEffect(() => {
    if (!previewReady) {
      return undefined;
    }

    const sectionId =
      selectedSection.id !== SECTIONS.NONE.id
        ? selectedSection.id
        : selectedSectionN;
    if (!sectionId || sectionId === SECTIONS.NONE.id) {
      return undefined;
    }

    const highlightTimer = window.setTimeout(() => {
      sendMessage("highlight", { sectionId });
      sendMessage("refreshSectionBadges", {});
    }, 150);

    return () => window.clearTimeout(highlightTimer);
  }, [
    previewReady,
    selectedSection.id,
    selectedSectionN,
    sendMessage,
  ]);

  useEffect(() => {
    if (!isViewSwitching) {
      return undefined;
    }

    const spinnerTimer = window.setTimeout(() => {
      setIsViewSwitching(false);
    }, 800);

    return () => window.clearTimeout(spinnerTimer);
  }, [isViewSwitching]);

  useEffect(() => {
    if (
      !previewReady ||
      selectedPage !== PAGES.GIVING_PAGE ||
      !isViewSwitching
    ) {
      return undefined;
    }

    const desktopFetchers = [
      fetchGivingBannerImage,
      fetchGivingBannerMainMessageDesktop,
      fetchGivingBannerMainMessageDesktopColor,
      fetchGivingBannerSecondaryMessageDesktop,
      fetchGivingBannerSecondaryMessageDesktopColor,
    ];
    const mobileFetchers = [
      fetchGivingBannerImageMobile,
      fetchGivingBannerMainMessageMobile,
      fetchGivingBannerMainMessageMobileColor,
      fetchGivingBannerSecondaryMessageMobile,
      fetchGivingBannerSecondaryMessageMobileColor,
    ];
    const fetchers = isMobileView ? mobileFetchers : desktopFetchers;

    const fetchTimer = window.setTimeout(() => {
      fetchers.forEach(fn => {
        if (typeof fn === "function") {
          fn();
        }
      });
    }, 600);

    return () => window.clearTimeout(fetchTimer);
  }, [
    previewReady,
    selectedPage,
    isViewSwitching,
    isMobileView,
    fetchGivingBannerImage,
    fetchGivingBannerImageMobile,
    fetchGivingBannerMainMessageDesktop,
    fetchGivingBannerMainMessageMobile,
    fetchGivingBannerMainMessageDesktopColor,
    fetchGivingBannerMainMessageMobileColor,
    fetchGivingBannerSecondaryMessageDesktop,
    fetchGivingBannerSecondaryMessageMobile,
    fetchGivingBannerSecondaryMessageDesktopColor,
    fetchGivingBannerSecondaryMessageMobileColor,
  ]);

  const documentTitle = useMemo(() => {
    const name =
      organizationName?.trim() ||
      charityName?.trim() ||
      String(customization?.organizationName || "").trim() ||
      "Charity";
    const sectionLabel = selectedSection.title || selectedPage;
    return `${name} Customization | ${sectionLabel}`;
  }, [
    organizationName,
    charityName,
    customization?.organizationName,
    selectedSection.title,
    selectedPage,
  ]);

  const renderPreviewIframe = () => {
    // Keep the same div > iframe shape in both branches so switching
    // isMobileView only updates props/className instead of forcing React
    // to unmount and remount the iframe (which would drop preview state
    // and reload donor-web from scratch).
    const shellClassName = isMobileView
      ? styles.mobilePreviewShell
      : styles.desktopPreviewShell;
    const shellStyle = isMobileView
      ? undefined
      : { transform: `scale(${desktopPreviewScale})` };
    const iframeClassName = isMobileView
      ? `${styles.iframeStyle} ${styles.phoneIframe}`
      : `${styles.iframeStyle} ${styles.desktopIframe}`;

    return (
      <div className={shellClassName} style={shellStyle}>
        <iframe
          key={iframeSrc}
          ref={iframeRef}
          src={iframeSrc}
          className={iframeClassName}
          onLoad={() => setIframeLoaded(true)}
        />
      </div>
    );
  };

  return (
    <>
      <Helmet title={documentTitle} />
      {previewLoading && (
        <div className={styles.spinnerContainer}>
          <ScaleLoader color="#1F65B1" />
        </div>
      )}
      <Container
        fluid
        className={`${styles.customizeContainer}`}
        style={previewLoading ? { display: "none" } : undefined}
      >
        {(previewReady || previewFailed) && (
          <TopBar
            pages={availablePages}
            selectedPage={selectedPage}
            onPageChange={handlePageChange}
            isMobileView={isMobileView}
            autoSaveStatus={previewReady ? autoSaveStatus : "idle"}
            saveAndPublish={saveAndPublish}
            isSavingAndPublishing={isSavingAndPublishing}
            onBackToSetup={handleBackToSetup}
            onSwitchView={handleSwitchView}
          />
        )}

        <Row className={styles.mainRow}>
          <Col md={3} className={styles.leftSection}>
            {isViewSwitching && selectedPage === PAGES.GIVING_PAGE && (
              <div className={styles.sidebarSpinnerOverlay}>
                <ScaleLoader color="#1F65B1" />
              </div>
            )}
            {previewReady && (
              <LeftSideBar
                selectedSection={selectedSection}
                variables={variablesPerSection[selectedSection.id]}
                charityId={charityId}
                firebase={firebase}
                onError={(msg) => showSnackbar(msg, "error")}
                postDetailLayout={
                  isPostDetailsPage
                    ? {
                        layout: postDetailLayout,
                        onLayoutChange: handlePostDetailLayoutChange,
                      }
                    : undefined
                }
              />
            )}
          </Col>
          <Col md={9} className={styles.rightSection}>
            {previewReady && (
              <p className={styles.editSectionTitle}>
                Select a section to edit
              </p>
            )}
            <div className={styles.previewFrame}>
              {isViewSwitching && (
                <div className={styles.previewSpinnerOverlay}>
                  <ScaleLoader color="#1F65B1" />
                </div>
              )}
              <div
                ref={desktopPreviewViewportRef}
                className={
                  isMobileView
                    ? `${styles.iframeContainer} ${styles.phoneViewport}`
                    : `${styles.iframeContainer} ${styles.desktopPreviewViewport}`
                }
              >
                {previewFailed && (
                  <PreviewUnavailablePanel
                    attemptedUrl={primaryOrigin}
                    onBack={handleBackToSetup}
                  />
                )}
                {iframeOrigin && !previewFailed && renderPreviewIframe()}
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      <SnackbarNotification
        {...snackbarProps}
        position={{ vertical: "top", horizontal: "center" }}
      />
    </>
  );
};

const mapStateToProps = state => {
  const { firebase, user } = state;

  const charityId = user.currentCharity.id;

  const whitelabelUrl = get(firebase, `data.${charityId}.config.whitelabelUrl`, null);
  const fallbackConfigLoaded = isLoaded(get(firebase, "data.global.config"));
  const customizationIframeOrigin = fallbackConfigLoaded
    ? get(firebase, `data.global.config.customizationIframeOrigin`, null)
    : undefined;

  const dataLoaded =
    charityId &&
    isLoaded(firebase.data[charityId]) &&
    isLoaded(firebase.data[charityId].charitySetup) &&
    isLoaded(firebase.data[charityId].charitySetup.customization) &&
    isLoaded(firebase.data[charityId].charitySetup.brandSetup);

  const customization = dataLoaded
    ? get(firebase, `data.${charityId}.charitySetup.customization`, null)
    : undefined;
  const brandSetup = dataLoaded
    ? get(firebase, `data.${charityId}.charitySetup.brandSetup`, null)
    : undefined;

  const paymentSetupLoaded =
    charityId &&
    isLoaded(firebase.data[charityId]) &&
    isLoaded(get(firebase, `data.${charityId}.charitySetup.payment`));

  const paymentSetupData = paymentSetupLoaded
    ? get(firebase, `data.${charityId}.charitySetup.payment`, null)
    : null;

  const customDonationUrl = get(firebase, `data.${charityId}.config.customDonationUrl`, null);

  return {
    charityId,
    charityName: user.currentCharity?.name || "",
    customization,
    brandSetup,
    whitelabelUrl,
    customizationIframeOrigin,
    fallbackConfigLoaded,
    usesExternalGivingPage: usesExternalGivingPage(paymentSetupData, customDonationUrl),
  };
};

const OnboardingCustomationLayout = serviceCaller(
  connect(mapStateToProps)(
    firebaseConnect((_, store) => {
      const charityId = store.getState().user.currentCharity.id;

      return store.getState().user.currentCharity.id !== ""
        ? [
            `${charityId}/charitySetup/customization`,
            `${charityId}/charitySetup/brandSetup`,
            `${charityId}/charitySetup/payment`,
            `${charityId}/config/whitelabelUrl`,
            `${charityId}/config/customDonationUrl`,
            `global/config`,
            `global/config/customizationIframeOrigin`,
          ]
        : [];
    })(withStyles(styles)(Layout))
  )
);

export default OnboardingCustomationLayout;
